import { useState, useEffect } from "react";
import {
  NavLink,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { addDisabledStateToMenu, isAdmin as checkIsAdmin } from "../../utils/permissions";

// iOS-style icon badge
function IosIcon({ icon, color, active, disabled }) {
  return (
    <span
      style={{
        width: 52,
        height: 52,
        borderRadius: 15,
        background: disabled
          ? "#c8c8cc"
          : active
          ? `linear-gradient(160deg, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0) 55%), linear-gradient(145deg, ${color}, ${adjustColor(color, -25)})`
          : `linear-gradient(160deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 55%), linear-gradient(145deg, ${color}, ${adjustColor(color, -20)})`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: disabled
          ? "none"
          : active
          ? `0 4px 14px ${color}66`
          : `0 2px 7px ${color}44`,
        flexShrink: 0,
        transition: "all 0.2s ease",
        transform: active ? "scale(1.08)" : "scale(1)",
      }}
    >
      <i
        className={icon}
        style={{
          color: "#fff",
          fontSize: 26,
          lineHeight: 1,
          display: "flex",
        }}
      />
    </span>
  );
}

// Darken a hex color by `amount` (0-255)
function adjustColor(hex, amount) {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0xff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

export default function Header({ isOpen }) {
  const [menuWithPermissions, setMenuWithPermissions] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = checkIsAdmin();

  const baseSidebarMenu = [
    {
      path: "/Homepage",
      text: "Home",
      icon: "ti ti-home",
      iosColor: "#2A5478",
    },
    {
      path: "/Admin/dashboard",
      text: "Dashboard",
      icon: "ti ti-layout-dashboard",
      iosColor: "#2A5478",
    },
    {
      path: "/Admin/patient",
      text: "Patients",
      icon: "ti ti-users",
      iosColor: "#2A5478",
      matchPaths: ["addPatient", "Manageappointment", "EditPatient"],
    },
    {
      path: "/Admin/appointment2",
      text: "Appointments",
      icon: "ti ti-calendar-event",
      iosColor: "#2A5478",
      matchPaths: ["Addappointment"],
    },
    {
      path: "/Admin/ManageLabs",
      text: "Labs",
      icon: "ti ti-flask",
      iosColor: "#2A5478",
    },
    {
      path: "/Admin/Billing",
      text: "Billing",
      icon: "ti ti-receipt",
      iosColor: "#2A5478",
    },
    {
      path: "/Admin/Pharmacy",
      text: "Pharmacy",
      icon: "ti ti-pill",
      iosColor: "#2A5478",
    },
    {
      path: "/Admin/Medicals",
      text: "Medicals",
      icon: "ti ti-heart-rate-monitor",
      iosColor: "#2A5478",
    },
    {
      path: "/Admin/Insurance",
      text: "Insurance",
      icon: "ti ti-shield-check",
      iosColor: "#2A5478",
    },
    {
      path: "/Admin/Service",
      text: "Services",
      icon: "ti ti-list-check",
      iosColor: "#2A5478",
    },
    {
      path: "/Admin/ReportsInc",
      text: "Reports",
      icon: "ti ti-chart-bar",
      iosColor: "#2A5478",
    },
  ];

  if (isAdmin) {
    baseSidebarMenu.push({
      path: "/Admin/setting",
      text: "Settings",
      icon: "ti ti-settings-2",
      iosColor: "#2A5478",
      matchPaths: ["/Admin/business_setting", "/Admin/finance"],
    });
  }

  useEffect(() => {
    const loadMenuPermissions = async () => {
      const menuWithState = await addDisabledStateToMenu(baseSidebarMenu);
      setMenuWithPermissions(menuWithState);
    };
    loadMenuPermissions();
  }, []);

  return (
    <nav className={`pc-sidebar ${isOpen ? "pc-sidebar-mobile-open" : ""}`}>
      <div className="navbar-wrapper">
        <div className="navbar-content">
          <ul className="pc-navbar">
            {menuWithPermissions.map((item, index) => {
              const isActive =
                location.pathname.includes(item.path) ||
                (item.matchPaths &&
                  item.matchPaths.some((sub) =>
                    location.pathname.includes(sub)
                  ));
              const isDisabled = item.disabled;

              return (
                <li className="pc-item" key={index}>
                  <NavLink
                    to={isDisabled ? "#" : item.path}
                    className={`pc-link ${isActive ? "active-link" : ""} ${isDisabled ? "disabled-link" : ""}`}
                    style={{
                      textDecoration: "none",
                      opacity: isDisabled ? 0.45 : 1,
                      pointerEvents: isDisabled ? "none" : "auto",
                      cursor: isDisabled ? "not-allowed" : "pointer",
                    }}
                    onClick={(e) => {
                      if (isDisabled) e.preventDefault();
                    }}
                  >
                    <IosIcon
                      icon={item.icon}
                      color={item.iosColor}
                      active={isActive}
                      disabled={isDisabled}
                    />
                    <span className="pc-mtext small" style={{ fontWeight: 600 }}>{item.text}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </nav>
  );
}
